/* Agent run viewer — visual replica of the old IntentDetail RunBoard,
 * but pointed at the new /api/agents/:id/runs/:runId/* endpoints.
 *
 * Polls the run while any step is in flight, renders each step with
 * status badges, inline PDF download chips, retry-on-failed, and an
 * answer-or-skip control for awaiting-human steps.
 */
import React from 'react';
import { Icon, Avatar } from './primitives.jsx';
import { MarkdownView } from './orgrepo-news-reports-arch.jsx';
import { getAgentRun, answerAgentRunStep, retryAgentRunStep, stopAgentRun, markAgentRunSeen } from '../api.js';
import { exportShellUrl } from '../lib/export-shell.ts';
import { publicOrigin } from '../lib/public-origin.ts';
import { useSubCrumbs } from './route-title-context.jsx';

const STEP_STATUS_COLOR = {
  pending:          { bg: 'var(--bg-card)', fg: 'var(--fg-muted)' },
  running:          { bg: '#FFE7DC',        fg: '#B83C0F' },
  'awaiting-human': { bg: '#FFE7DC',        fg: '#B83C0F' },
  done:             { bg: '#DCF1E2',        fg: '#00702A' },
  failed:           { bg: '#FCE6E0',        fg: '#A20235' },
  skipped:          { bg: 'var(--bg-card)', fg: 'var(--fg-muted)' },
};

function relTime(value) {
  if (!value) return '—';
  const d = new Date(value);
  const abs = Math.abs(Date.now() - d.getTime());
  const min = 60_000, hr = 60 * min, day = 24 * hr;
  if (abs < min)     return 'just now';
  if (abs < hr)      return `${Math.round(abs / min)}m ago`;
  if (abs < day)     return `${Math.round(abs / hr)}h ago`;
  if (abs < 7 * day) return `${Math.round(abs / day)}d ago`;
  return d.toLocaleDateString();
}

/**
 * Derive a `.md` download filename from a converted file's Markdown.
 * The `file-to-markdown` skill opens every conversion with a
 * `## <displayName>` source header, so we read that line and swap the
 * source extension (report.pdf → report.md). Falls back to a numbered
 * name when there's no header.
 */
function mdFilenameFromHeader(markdown, index) {
  const m = /^##\s+(.+?)\s*$/m.exec(markdown || '');
  const raw = (m ? m[1] : '').trim();
  const base = raw.replace(/\.[a-z0-9]{1,8}$/i, '').trim();
  const slug = base.replace(/[\\/:*?"<>|]+/g, '-').replace(/\s+/g, ' ').trim();
  return `${slug || `converted-${index + 1}`}.md`;
}

/**
 * Collect the converted-Markdown deliverables from a run's steps. The
 * Markdown Converter agent runs the `file-to-markdown` skill once per
 * uploaded file; each done skill step's resultText IS that file's clean
 * Markdown. We read the skill steps directly (rather than the LLM's
 * assembled final message) so the per-file split + download stay robust
 * regardless of how the agent concatenated them.
 *
 * Returns { files: [{ name, markdown }], combined } — `combined` joins
 * every file with `---` separators under a `# Converted files` heading
 * (the one-click "download all"). Empty `files` when the run produced
 * no file-to-markdown output (every non-converter run).
 */
function collectMarkdownArtifacts(steps) {
  const files = (steps || [])
    .filter(s => s.status === 'done' && s.type === 'skill' && s.skillId === 'file-to-markdown' && (s.resultText || '').trim())
    .map((s, i) => ({ name: mdFilenameFromHeader(s.resultText, i), markdown: s.resultText.trim() }));
  const combined = files.length > 1
    ? `# Converted files\n\n${files.map(f => f.markdown).join('\n\n---\n\n')}\n`
    : (files[0]?.markdown || '');
  return { files, combined };
}

/** Trigger a client-side download of `text` as a Markdown file named
 *  `name`. The converted Markdown already lives in the polled run data,
 *  so no round-trip / R2 object is needed — mint a Blob and click it
 *  (same pattern as the Manual's "Download Markdown"). */
function downloadTextFile(name, text) {
  const blob = new Blob([text], { type: 'text/markdown;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 60000);
}

/**
 * Result surface for a Markdown Converter run: a Download button per
 * converted file (plus "Download all" when several), and the converted
 * Markdown rendered inline so it can be read / copied without opening
 * each step's collapsed "View skill output". Rendered in the run
 * detail's right pane (which otherwise shows no preview for this agent).
 */
const MarkdownResultPanel = ({ artifacts }) => {
  const { files, combined } = artifacts;
  if (!files.length) return null;
  const combinedName = files.length > 1 ? 'converted-files.md' : files[0].name;
  return (
    <div style={{display:'flex',flexDirection:'column',gap:12,minHeight:0}}>
      <div style={{display:'flex',flexWrap:'wrap',gap:8,alignItems:'center'}}>
        <button
          type="button"
          className="btn-orange"
          onClick={() => downloadTextFile(combinedName, combined)}
          style={{padding:'8px 16px',borderRadius:999,fontSize:13,fontWeight:600}}
          title="Download the converted Markdown to your device"
        >⤓ Download Markdown{files.length > 1 ? ' (all)' : ''}</button>
        {files.length > 1 && files.map((f, i) => (
          <button
            key={i}
            type="button"
            className="btn-ghost"
            onClick={() => downloadTextFile(f.name, f.markdown)}
            style={{padding:'7px 12px',borderRadius:999,fontSize:12}}
            title={`Download just ${f.name}`}
          >⤓ {f.name}</button>
        ))}
      </div>
      <div style={{background:'#fff',border:'1px solid var(--border)',borderRadius:10,padding:'14px 16px',fontSize:13,lineHeight:1.55,overflow:'auto',flex:1,minHeight:280}}>
        <MarkdownView source={combined}/>
      </div>
    </div>
  );
};

/**
 * Split a run's `description` into the user-chosen settings (the
 * structured intake block the run-now endpoint prepends) and the
 * agent's prompt template. The run-now path joins them with a
 * `\n\n---\n\n` separator and prefixes the intake with a
 * "USER-PROVIDED INTAKE …" marker line; runs without an intake form
 * carry the prompt template alone. Returns `{ settings, prompt }`
 * where `settings` is null when no intake block is present.
 */
const INTAKE_MARKER = 'USER-PROVIDED INTAKE';
const INTAKE_SEPARATOR = '\n\n---\n\n';
function splitRunDescription(description) {
  const desc = (description || '').trim();
  const sepIdx = desc.indexOf(INTAKE_SEPARATOR);
  if (sepIdx > 0 && desc.slice(0, sepIdx).includes(INTAKE_MARKER)) {
    const settings = desc
      .slice(0, sepIdx)
      // Drop the marker line itself — keep just the answered fields.
      .replace(/^USER-PROVIDED INTAKE[^\n]*\n+/i, '')
      .trim();
    return { settings: settings || null, prompt: desc.slice(sepIdx + INTAKE_SEPARATOR.length).trim() };
  }
  return { settings: null, prompt: desc };
}

/**
 * Step 1 — the prompt + settings the run was launched with. Rendered as
 * the first row of the step list with the SAME layout as the real steps
 * (StepBoardItem): a static title line, a badge row whose first chip is
 * the step number ("step 1"), and the settings + agent prompt tucked in
 * a collapsed `<details>` exactly like a real step's "View skill output".
 * Present only when the run carries a description; the real steps then
 * continue from "step 2".
 */
const RunInputsPanel = ({ description }) => {
  if (!description) return null;
  const { settings, prompt } = splitRunDescription(description);
  if (!settings && !prompt) return null;
  return (
    <li>
      <strong style={{display:'block',fontSize:13,wordBreak:'break-word',lineHeight:1.35,marginBottom:4}}>Prompt &amp; settings used</strong>
      <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:4,flexWrap:'wrap'}}>
        <span style={{fontSize:10,padding:'2px 7px',borderRadius:4,background:'var(--bg-alt)',color:'var(--fg-muted)',textTransform:'uppercase',letterSpacing:'0.04em',fontWeight:600,flexShrink:0}}>step 1</span>
        <span className="tag" style={{fontSize:10,flexShrink:0}}>inputs</span>
      </div>
      <details>
        <summary style={{fontSize:11,color:'var(--orange)',cursor:'pointer'}}>View prompt &amp; settings</summary>
        <div style={{marginTop:8}}>
          {settings && (
            <div style={{marginBottom:prompt ? 8 : 0}}>
              <div style={{fontSize:10.5,letterSpacing:'0.06em',textTransform:'uppercase',color:'var(--fg-muted)',marginBottom:4,fontWeight:600}}>Settings entered</div>
              <div style={{padding:10,background:'var(--bg)',border:'1px solid var(--border)',borderRadius:6,fontSize:13,lineHeight:1.5,maxHeight:320,overflow:'auto'}}>
                <MarkdownView source={settings}/>
              </div>
            </div>
          )}
          {prompt && (
            <div>
              <div style={{fontSize:10.5,letterSpacing:'0.06em',textTransform:'uppercase',color:'var(--fg-muted)',marginBottom:4,fontWeight:600}}>{settings ? 'Agent prompt' : 'Prompt'} ({prompt.length} chars)</div>
              <div style={{padding:10,background:'var(--bg)',border:'1px solid var(--border)',borderRadius:6,fontSize:13,lineHeight:1.5,maxHeight:360,overflow:'auto'}}>
                <MarkdownView source={prompt}/>
              </div>
            </div>
          )}
        </div>
      </details>
    </li>
  );
};

/**
 * Wraps the inline RunBoard with the fetch-and-poll loop. Pass
 * `agentId` (for the endpoints) and `runId` (the agent_runs.id).
 */
export const AgentRunInline = ({ agentId, runId, onRunComplete, onDelete = null, onCollapse = null }) => {
  const [data, setData] = React.useState(null);
  const [error, setError] = React.useState(null);
  const [reloadKey, setReloadKey] = React.useState(0);
  // Track the running→terminal handoff so we fire onRunComplete exactly
  // once, letting the parent (run-history pill + agent tile) refresh
  // away from the stale "running" the spawn bookmark was frozen at.
  const sawRunningRef = React.useRef(false);

  React.useEffect(() => {
    let cancelled = false;
    let timer = null;
    const tick = async () => {
      try {
        const result = await getAgentRun(agentId, runId);
        if (cancelled) return;
        setData(result);
        // Keep polling until the RUN itself lands a terminal state — not
        // just until the last step stops. run.status flips to done/failed a
        // beat after the final step finishes, and the deck/memo CTA gates on
        // run.status === 'done'; stopping at "no in-flight steps" would leave
        // the "Open viewer" button stuck disabled until a manual reload.
        const runTerminal = result.run?.status === 'done' || result.run?.status === 'failed';
        const stepsInFlight = (result.steps || []).some(s =>
          s.status === 'pending' || s.status === 'running' || s.status === 'awaiting-human'
        );
        const stillRunning = !runTerminal || stepsInFlight;
        if (stillRunning) {
          sawRunningRef.current = true;
          if (!cancelled) timer = setTimeout(tick, 6000);
        } else if (sawRunningRef.current) {
          // Run was in flight on a prior tick and has now landed a
          // terminal state — nudge the parent to re-fetch.
          sawRunningRef.current = false;
          onRunComplete?.();
        }
      } catch (err) {
        if (!cancelled) setError(err.message);
      }
    };
    tick();
    return () => { cancelled = true; if (timer) clearTimeout(timer); };
  }, [agentId, runId, reloadKey, onRunComplete]);

  if (error) return <div style={{fontSize:12,color:'#A20235',marginTop:6}}>Run fetch failed: {error}</div>;
  if (!data) return <div style={{fontSize:12,color:'var(--fg-muted)',marginTop:6,padding:8}}>Loading run steps…</div>;
  if (!data.run) return <div style={{fontSize:12,color:'var(--fg-muted)',marginTop:6,padding:8}}>This run is still queued — first step will start in a moment.</div>;

  return (
    <RunBoard
      agentId={agentId}
      run={data.run}
      steps={data.steps || []}
      reminders={data.reminders || []}
      viewer={data.viewer || null}
      eta={data.eta || null}
      onChanged={() => setReloadKey(k => k + 1)}
      onDelete={onDelete}
      onCollapse={onCollapse}
    />
  );
};

// Compact status badge shown over the run-history preview thumbnail.
// Reflects where the deck is in its lifecycle: generation progress while
// slides are being designed, then approval progress once the user starts
// signing slides off (e.g. "3/12 generated" → "All generated" →
// "2/12 approved" → "All approved").
function deckStatusBadge(viewer) {
  const total = viewer.blockCount || 0;
  const ready = viewer.readyCount ?? 0;
  const approved = viewer.approvedCount || 0;
  const green = { bg: '#DCF1E2', fg: '#00702A' };
  const orange = { bg: '#FFE7DC', fg: '#B83C0F' };
  if (viewer.kind !== 'deck') {
    return total > 0 && ready >= total ? { text: 'All ready', ...green } : { text: `${ready}/${total} ready`, ...orange };
  }
  if (total > 0 && approved >= total) return { text: 'All approved', ...green };
  if (approved > 0)                   return { text: `${approved}/${total} approved`, ...green };
  if (total > 0 && ready >= total)    return { text: 'All generated', ...orange };
  return { text: `${ready}/${total} generated`, ...orange };
}

// Shared CTA buttons for a deck/memo run — used in the run-history card
// (with "See details") and in the fullscreen detail view (without). The
// "Open viewer" button stays visible but is disabled until the run's
// automated process (generation + the agent's own checks) has finished —
// i.e. the run reached `done` — so the viewer is only opened for the
// manual-review step once there's a finished deck to review. Export PDF /
// PPTX / Share appear once every slide is approved (same gate the viewer
// uses to unlock export).
function DeckCtaButtons({ agentId, run, viewer, title, generationDone, onSeeDetails }) {
  const [shareUrl, setShareUrl] = React.useState(null);
  const [shareBusy, setShareBusy] = React.useState(false);
  const [shareError, setShareError] = React.useState(null);
  const [shareCopied, setShareCopied] = React.useState(false);
  if (!viewer) return null;
  const isDeck = viewer.kind === 'deck';
  const blockCount = viewer.blockCount || 0;
  const approvedCount = viewer.approvedCount || 0;
  const allApproved = isDeck && blockCount > 0 && approvedCount >= blockCount;
  const deckTitle = title || (isDeck ? 'Slide deck' : 'Memo');
  const viewerLabel = `Open ${isDeck ? 'deck viewer' : 'memo viewer'} →`;
  // Deck viewer lives under the agent/run route so it sits in the
  // breadcrumb trail; memo keeps its legacy #runs/<id>/memo route.
  const viewerHref = isDeck ? `#${runDetailHash(agentId, run.id)}/deck` : viewer.url;
  const pill = { padding: '9px 18px', borderRadius: 999, fontSize: 13, fontWeight: 600 };

  const mintShareLink = async () => {
    setShareError(null);
    setShareBusy(true);
    try {
      const res = await fetch('/api/share-tokens', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ target: 'agent-run', id: run.id, kind: 'deck' }),
      });
      const j = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(j.error || `share → ${res.status}`);
      const abs = new URL(j.url, publicOrigin()).toString();
      setShareUrl(abs);
      setShareCopied(false);
      try { await navigator.clipboard.writeText(abs); setShareCopied(true); }
      catch { /* clipboard blocked — the URL row below is still selectable */ }
    } catch (err) {
      setShareError(err.message || String(err));
    } finally {
      setShareBusy(false);
    }
  };

  return (
    <>
      <div style={{display:'flex',flexWrap:'wrap',gap:8}}>
        {onSeeDetails && (
          <button type="button" className="btn-ghost" onClick={onSeeDetails} style={{...pill}}>See details</button>
        )}
        {generationDone ? (
          <a href={viewerHref} onClick={() => markAgentRunSeen(agentId, run.id)} className="btn-orange" style={{...pill,textDecoration:'none',textAlign:'center'}}>{viewerLabel}</a>
        ) : (
          <button
            type="button"
            disabled
            className="btn-orange"
            style={{...pill,opacity:0.5,cursor:'not-allowed'}}
            title={`Available once all ${isDeck ? 'slides' : 'sections'} are generated and checked.`}
          >{viewerLabel}</button>
        )}
        {allApproved && (
          <>
            <a
              href={exportShellUrl({ src: `/api/agents/${agentId}/runs/${run.id}/deck/export/pdf`, kind: 'pdf', title: deckTitle })}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-orange"
              style={{...pill,textDecoration:'none',textAlign:'center'}}
              title="Open the designed slides in a print-ready tab and Save as PDF — one 16:9 page per slide."
            >Export PDF</a>
            <a
              href={exportShellUrl({ src: `/api/agents/${agentId}/runs/${run.id}/deck/export/pptx`, kind: 'pptx', title: deckTitle })}
              className="btn-orange"
              style={{...pill,textDecoration:'none',textAlign:'center'}}
              title="Export a 1:1 PowerPoint — each slide matches the PDF, with titles and body as native, editable text boxes."
            >Export PPTX</a>
            <button
              type="button"
              className="btn-ghost"
              disabled={shareBusy}
              onClick={mintShareLink}
              style={{...pill}}
              title="Mint a public read-only HTML link anyone can open without an Eraneos login, and copy it to your clipboard."
            >{shareBusy ? 'Generating…' : (shareCopied ? 'Link copied ✓' : 'Share html link')}</button>
          </>
        )}
      </div>
      {shareError && (
        <div style={{marginTop:8,padding:'8px 12px',background:'#FCE6E0',color:'#A20235',borderRadius:6,fontSize:12.5}}>
          Couldn't create share link: {shareError}
        </div>
      )}
      {shareUrl && (
        <div style={{marginTop:8,padding:'10px 12px',background:'var(--bg)',border:'1px solid var(--border)',borderRadius:8,display:'flex',alignItems:'center',gap:8,flexWrap:'wrap'}} role="status">
          <span style={{fontSize:12,color:'var(--fg-muted)',whiteSpace:'nowrap'}}>Read-only link:</span>
          <code style={{flex:'1 1 200px',fontSize:12,padding:'4px 8px',background:'var(--bg-alt)',borderRadius:4,wordBreak:'break-all'}}>{shareUrl}</code>
          <button
            type="button"
            className="btn-orange"
            onClick={async () => { try { await navigator.clipboard.writeText(shareUrl); setShareCopied(true); } catch { /* selectable above */ } }}
            style={{padding:'5px 12px',fontSize:12,borderRadius:999}}
          >{shareCopied ? 'Copied ✓' : 'Copy'}</button>
        </div>
      )}
    </>
  );
}

// Derived display state for a run — shared by the living card (RunBoard)
// and the routed detail view (RunDetailContent) so both render the exact
// same status line, progress, title and preview URL.
function computeRunDisplay(agentId, run, steps, viewer) {
  const isTerminal = run.status === 'done' || run.status === 'failed';
  // Binary artefact URLs (PDF / DOCX / PPTX / XLSX) from the last finished step.
  const finalStep = [...steps].reverse().find(s => s.status === 'done' && s.resultText) || null;
  const finalArtefacts = finalStep
    ? Array.from(finalStep.resultText.matchAll(/\((\/api\/photos\/u\/[^)]+\.(pdf|docx|pptx|xlsx))\)/g))
        .map(m => ({ url: m[1], ext: m[2].toLowerCase() }))
    : [];

  // The step list prepends the "Prompt & settings used" inputs row as
  // "step 1" whenever the run carries a description, pushing real steps to
  // 2..N+1, so "step X of Y" lines up with each row's own step-number badge.
  const hasInputsRow = (() => {
    if (!run.description) return false;
    const { settings, prompt } = splitRunDescription(run.description);
    return !!(settings || prompt);
  })();
  const inputsRows = hasInputsRow ? 1 : 0;
  const totalSteps = steps.length + inputsRows;
  const currentStep = Math.min((run.currentStepIndex ?? 0) + 1 + inputsRows, totalSteps);

  const sc = run.status === 'done' ? { bg:'#DCF1E2', fg:'#00702A' }
           : run.status === 'failed' ? { bg:'#FCE6E0', fg:'#A20235' }
           : { bg:'#FFE7DC', fg:'#B83C0F' };
  const title = run.title
    || (run.description ? splitRunDescription(run.description).prompt.split('\n')[0].trim().slice(0, 100) : '')
    || 'Agent run';
  const runningStep = steps.find(s => s.status === 'running');
  const awaitingStep = steps.find(s => s.status === 'awaiting-human');
  const failedStep = [...steps].reverse().find(s => s.status === 'failed');
  // Progress reflects *completed* work, not the index of the active step.
  // `current_step_index` already points at the final step the moment it
  // *starts*, so deriving the bar from it made a still-running run read 100%
  // (e.g. while the deck-reviewer "check" pass was mid-flight). Instead count
  // finished steps, give the in-flight step half credit, treat the static
  // inputs row as already complete, and reserve 100% for a finished run.
  const doneSteps = steps.filter(s => s.status === 'done').length;
  const completedUnits = doneSteps + inputsRows + (runningStep ? 0.5 : 0);
  let pct = totalSteps ? Math.round((completedUnits / totalSteps) * 100) : 0;
  if (run.status === 'done') pct = 100;
  else if (!isTerminal) pct = Math.min(pct, 99);
  const deckProgress = viewer?.kind === 'deck' && viewer.blockCount
    ? `slide ${Math.min((viewer.readyCount || 0) + 1, viewer.blockCount)} of ${viewer.blockCount}`
    : null;

  // The single live line that updates in place.
  let liveMain, liveSub = null, liveColor = 'var(--orange)';
  if (run.status === 'failed') {
    liveMain = run.errorMessage || failedStep?.errorMessage || 'Run failed';
    liveColor = '#A20235';
  } else if (run.status === 'done') {
    liveMain = `Done — ${steps.length} step${steps.length === 1 ? '' : 's'}`
      + (viewer ? ` · ${viewer.blockCount} ${viewer.kind === 'deck' ? 'slides' : 'sections'}` : '');
    liveColor = '#00702A';
  } else if (awaitingStep) {
    liveMain = awaitingStep.name || 'Needs your approval';
    liveSub = 'Review and approve to continue';
    liveColor = '#B83C0F';
  } else if (runningStep) {
    liveMain = runningStep.name + (deckProgress ? ` · ${deckProgress}` : '');
    liveSub = runningStep.skillId ? `skill: ${runningStep.skillId}` : null;
  } else {
    liveMain = 'Queued — the first step is starting…';
  }

  // chrome=0 drops the deck preview's in-iframe toolbar — the run viewer
  // supplies its own header and export controls. While a deck is still
  // generating we (a) bust the iframe with a progress signature so the
  // embed re-fetches and shows freshly persisted slides without a page
  // reload, and (b) ask the preview to smooth-scroll the most-recent slide
  // to the top (focus=last) so the slide just finished is always on screen.
  const deckLive = viewer?.kind === 'deck' && !isTerminal;
  const previewVersion = viewer
    ? `${viewer.readyCount || 0}.${viewer.approvedCount || 0}.${viewer.generatedCount || 0}.${run.status}`
    : '';
  const previewUrl = viewer
    ? (viewer.kind === 'deck'
        ? `/api/agents/${agentId}/runs/${run.id}/deck/preview?chrome=0${deckLive ? '&focus=last' : ''}&v=${encodeURIComponent(previewVersion)}`
        : `/api/agents/${agentId}/runs/${run.id}/${viewer.kind}/preview`)
    : null;

  return { isTerminal, finalArtefacts, inputsRows, totalSteps, currentStep, sc, title, awaitingStep, failedStep, pct, liveMain, liveSub, liveColor, previewUrl };
}

// Navigate to a run's routed detail view (replaces the old modal).
function runDetailHash(agentId, runId) { return `agents/${encodeURIComponent(agentId)}/${encodeURIComponent(runId)}`; }

export const RunBoard = ({ agentId, run, steps, reminders = [], viewer = null, eta = null, onChanged, onDelete = null, onCollapse = null }) => {
  const {
    isTerminal, finalArtefacts, totalSteps, currentStep, sc, title,
    awaitingStep, failedStep, pct, liveMain, liveSub, liveColor, previewUrl,
  } = computeRunDisplay(agentId, run, steps, viewer);
  // Converted-Markdown deliverables (Markdown Converter runs) — surfaced
  // as download chips on the card so the .md is reachable without opening
  // the run detail. Empty for every other run.
  const mdArtifacts = collectMarkdownArtifacts(steps);
  const openDetail = () => { location.hash = runDetailHash(agentId, run.id); };
  // History-card thumbnail: the chrome-less single-slide embed (no deck
  // padding) so it stays a crisp miniature at agent-card size. The full
  // `previewUrl` keeps the scrolling chrome=0 view for the detail pane.
  const thumbUrl = viewer ? `/api/agents/${agentId}/runs/${run.id}/${viewer.kind}/preview?embed=1` : null;
  // On a finished deck/memo card the one-line summary ("Done — N steps ·
  // M slides") moves down into the preview row, grouped with the CTA
  // buttons and vertically centred against the thumbnail — so the card has
  // no dead band beside the (taller) thumbnail and no redundant full-width
  // line above. Every other state keeps the standalone live line.
  const inlineSummary = !!previewUrl && run.status === 'done';

  const stopRun = async () => {
    if (!window.confirm('Stop this agent run? Any work in progress will be marked failed.')) return;
    try { await stopAgentRun(agentId, run.id, { scope: 'run' }); onChanged?.(); }
    catch (err) { window.alert(`Stop failed: ${err.message || String(err)}`); }
  };

  // A finished deck/memo card (twoCol) lays out as two columns: ALL the
  // text — meta, title, summary and the CTA buttons — in column 1, and the
  // preview filling the full height as column 2. On phones the two columns
  // wrap to a single stack: text on top (buttons on one line), preview
  // full-width below. Every other state keeps the meta/title stacked at
  // the top of the card, so they're extracted here to be reused in both.
  const twoCol = inlineSummary;

  const metaLine = (
    <div style={{display:'flex',alignItems:'center',gap:8,flexWrap:'wrap',marginBottom:6}}>
      <span style={{fontSize:10.5,padding:'2px 8px',borderRadius:4,background:sc.bg,color:sc.fg,textTransform:'uppercase',letterSpacing:'0.04em',fontWeight:600}}>{run.status}</span>
      <span style={{fontSize:12,color:'var(--fg-muted)'}}>started {relTime(run.startedAt)}</span>
      {run.completedAt && <span style={{fontSize:12,color:'var(--fg-muted)'}}>· finished {relTime(run.completedAt)}</span>}
      <div style={{flex:1}}/>
      {!isTerminal && eta?.totalLabel && (
        <em style={{fontSize:11.5,color:'var(--orange)',fontStyle:'italic'}}>≈ {eta.totalLabel} total</em>
      )}
      <span style={{fontSize:11.5,color:'var(--fg-muted)'}}>step {currentStep} of {totalSteps}</span>
      {onCollapse && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onCollapse(); }}
          title="Collapse"
          aria-label="Collapse run"
          style={{all:'unset',cursor:'pointer',color:'var(--fg-muted)',flexShrink:0,lineHeight:0,padding:2,borderRadius:6}}
        >
          <Icon name="collapse" size={15}/>
        </button>
      )}
    </div>
  );

  const titleRow = (
    <div style={{display:'flex',alignItems:'flex-start',gap:10,marginBottom:twoCol?10:6}}>
      {(run.ownerName || run.ownerPhotoUrl) && (
        <span style={{marginTop:2,flexShrink:0}}>
          <Avatar src={run.ownerPhotoUrl} name={run.ownerName} size={22} title={run.ownerName || undefined}/>
        </span>
      )}
      <div style={{flex:1,minWidth:0,fontSize:18,fontWeight:600,lineHeight:1.2,letterSpacing:'-0.01em',wordBreak:'break-word'}}>{title}</div>
      {/* Stop sits beside the title while the run is in flight — a labelled
          pill on desktop, icon-only (the ■ glyph) on phones (see CSS). */}
      {!isTerminal && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); stopRun(); }}
          title="Halt the whole run."
          aria-label="Stop run"
          style={{display:'inline-flex',alignItems:'center',gap:6,cursor:'pointer',fontSize:12.5,fontWeight:600,color:'#A20235',padding:'5px 12px',borderRadius:999,border:'1px solid #FCE6E0',background:'#FCE6E0',flexShrink:0,lineHeight:1,marginTop:1}}
        >
          <span aria-hidden="true">■</span>
          <span className="run-stop__label">Stop run</span>
        </button>
      )}
      {onDelete && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onDelete(); }}
          title="Delete this run"
          aria-label="Delete this run"
          style={{all:'unset',cursor:'pointer',color:'var(--fg-muted)',flexShrink:0,lineHeight:0,padding:2,borderRadius:6,marginTop:3}}
        >
          <Icon name="trash" size={16}/>
        </button>
      )}
    </div>
  );

  return (
    <div style={{border:'1px solid var(--border)',borderRadius:12,overflow:'hidden',background:'var(--bg)'}}>
      <div style={{padding:'14px 16px'}}>
        {!twoCol && metaLine}
        {!twoCol && titleRow}

        {/* live activity line — updates in place. On a finished deck/memo
            card this moves into the preview row (see inlineSummary). */}
        {!inlineSummary && (
          <div style={{display:'flex',alignItems:'flex-start',gap:9,marginBottom:isTerminal?0:10}}>
            {!isTerminal && <span style={{width:9,height:9,borderRadius:'50%',background:liveColor,marginTop:5,flexShrink:0}}/>}
            <div style={{minWidth:0}}>
              <div style={{fontSize:14,fontWeight:500,color:liveColor,wordBreak:'break-word'}}>{liveMain}</div>
              {liveSub && <div style={{fontSize:12.5,color:'var(--fg-muted)',marginTop:2,fontFamily:liveSub.startsWith('skill:')?'JetBrains Mono':'inherit'}}>{liveSub}</div>}
            </div>
          </div>
        )}

        {/* progress bar (in-flight only) */}
        {!isTerminal && (
          <div style={{margin:'4px 0 12px'}}>
            <div style={{height:5,borderRadius:3,background:'var(--bg-card)',overflow:'hidden'}}>
              <div style={{height:'100%',width:`${pct}%`,background:'var(--orange)',borderRadius:3,transition:'width 0.3s'}}/>
            </div>
            <div style={{fontSize:11,color:'var(--fg-muted)',marginTop:5}}>{pct}%</div>
          </div>
        )}

        {/* CTA: approval gate */}
        {awaitingStep && (
          <div style={{background:'#FFE7DC',borderRadius:10,padding:'10px 12px',marginBottom:12}}>
            <div style={{fontSize:13,fontWeight:600,color:'#B83C0F',marginBottom:6}}>⚑ {awaitingStep.name || 'Your approval is needed'}</div>
            <HumanAnswerForm agentId={agentId} runId={run.id} step={awaitingStep} onAnswered={onChanged}/>
          </div>
        )}

        {/* CTA: failed → re-run from the failed step */}
        {run.status === 'failed' && failedStep && (
          <div style={{marginBottom:12}}>
            <RetryButton agentId={agentId} runId={run.id} stepRunId={failedStep.id} onRetried={onChanged} label="↻ Re-run from failed step"/>
          </div>
        )}

        {/* result preview. Finished deck/memo (twoCol): two columns — all
            the text + buttons on the left, the preview filling the full
            height on the right; they stack on phones (preview full-width
            below). In-flight-with-preview keeps the small thumbnail tucked
            to the right of the CTA buttons. */}
        {previewUrl && (
          twoCol ? (
            <div style={{display:'flex',gap:16,flexWrap:'wrap',alignItems:'stretch'}}>
              <div style={{flex:'1 1 260px',minWidth:0,display:'flex',flexDirection:'column'}}>
                {metaLine}
                {titleRow}
                <div style={{fontSize:14,fontWeight:500,color:liveColor,wordBreak:'break-word',marginBottom:12}}>{liveMain}</div>
                <div style={{flexGrow:1,minHeight:6}}/>
                {viewer && (
                  <DeckCtaButtons
                    agentId={agentId}
                    run={run}
                    viewer={viewer}
                    title={title}
                    generationDone={run.status === 'done'}
                    onSeeDetails={openDetail}
                  />
                )}
              </div>
              <div
                onClick={openDetail}
                title="Open full preview"
                style={{flex:'1 1 320px',minWidth:0,alignSelf:'stretch',minHeight:220,position:'relative',borderRadius:10,overflow:'hidden',cursor:'pointer',background:'#fff',boxShadow:'0 1px 4px rgba(0,0,0,0.12)'}}
              >
                <iframe title="result preview" src={thumbUrl} sandbox="allow-same-origin allow-scripts" style={{position:'absolute',inset:0,width:'100%',height:'100%',border:0,pointerEvents:'none'}}/>
                {viewer && (() => {
                  const b = deckStatusBadge(viewer);
                  return (
                    <div style={{position:'absolute',top:8,right:8,background:b.bg,color:b.fg,fontSize:10,fontWeight:700,padding:'2px 8px',borderRadius:999,boxShadow:'0 1px 4px rgba(0,0,0,0.18)',pointerEvents:'none'}}>{b.text}</div>
                  );
                })()}
              </div>
            </div>
          ) : (
            <div style={{display:'flex',gap:14,alignItems:'center',marginBottom:4}}>
              {viewer && (
                <div style={{flex:1,minWidth:0,display:'flex',flexDirection:'column',gap:9}}>
                  <DeckCtaButtons
                    agentId={agentId}
                    run={run}
                    viewer={viewer}
                    title={title}
                    generationDone={run.status === 'done'}
                    onSeeDetails={openDetail}
                  />
                </div>
              )}
              <div
                onClick={openDetail}
                title="Open full preview"
                style={{flexShrink:0,width:200,maxWidth:'42%',aspectRatio:'16 / 9',position:'relative',borderRadius:10,overflow:'hidden',cursor:'pointer',background:'#fff',boxShadow:'0 1px 4px rgba(0,0,0,0.12)'}}
              >
                <iframe title="result preview" src={thumbUrl} sandbox="allow-same-origin allow-scripts" style={{position:'absolute',inset:0,width:'100%',height:'100%',border:0,pointerEvents:'none'}}/>
                {viewer && (() => {
                  const b = deckStatusBadge(viewer);
                  return (
                    <div style={{position:'absolute',top:6,right:6,background:b.bg,color:b.fg,fontSize:9.5,fontWeight:700,padding:'2px 7px',borderRadius:999,boxShadow:'0 1px 4px rgba(0,0,0,0.18)',pointerEvents:'none'}}>{b.text}</div>
                  );
                })()}
              </div>
            </div>
          )
        )}
      </div>

      {/* converted-Markdown chips — the Markdown Converter's deliverable.
          "Download all" plus one chip per file, so the .md is reachable
          straight from the card. */}
      {mdArtifacts.files.length > 0 && (
        <div style={{padding:'12px 16px',display:'flex',flexWrap:'wrap',gap:8,alignItems:'center',borderTop:'1px solid var(--border)'}}>
          <button
            type="button"
            onClick={() => downloadTextFile(mdArtifacts.files.length > 1 ? 'converted-files.md' : mdArtifacts.files[0].name, mdArtifacts.combined)}
            style={{display:'inline-flex',alignItems:'center',gap:8,padding:'8px 14px',borderRadius:999,background:'var(--orange)',color:'#fff',border:0,cursor:'pointer',fontSize:13,fontWeight:600}}
            title="Download the converted Markdown to your device"
          >
            <Icon name="file" size={13}/> Download Markdown{mdArtifacts.files.length > 1 ? ' (all)' : ''}
          </button>
          {mdArtifacts.files.length > 1 && mdArtifacts.files.map((f, i) => (
            <button
              key={i}
              type="button"
              onClick={() => downloadTextFile(f.name, f.markdown)}
              className="btn-ghost"
              style={{display:'inline-flex',alignItems:'center',gap:6,padding:'7px 12px',borderRadius:999,fontSize:12}}
              title={`Download just ${f.name}`}
            >⤓ {f.name}</button>
          ))}
        </div>
      )}

      {/* artefact chips */}
      {finalArtefacts.length > 0 && (
        <div style={{padding:'12px 16px',display:'flex',flexWrap:'wrap',gap:8,borderTop:'1px solid var(--border)'}}>
          {finalArtefacts.map(({ url, ext }) => {
            const label = ext === 'pdf' ? 'Open PDF' : ext === 'docx' ? 'Download Word' : ext === 'pptx' ? 'Download PowerPoint' : ext === 'xlsx' ? 'Download Excel' : 'Download';
            const target = ext === 'pdf' ? '_blank' : undefined;
            return (
              <a key={url} href={url} target={target} rel="noopener noreferrer" style={{display:'inline-flex',alignItems:'center',gap:8,padding:'8px 14px',borderRadius:999,background:'#A20235',color:'#fff',textDecoration:'none',fontSize:13,fontWeight:600}}>
                <Icon name="file" size={13}/> {label}
              </a>
            );
          })}
        </div>
      )}

      <RemindersPanel reminders={reminders}/>

      {/* action bar — for runs without a deck/memo preview, "See details"
          is the way into the detail view (deck/memo runs use "See details"
          under the preview instead). Stop now lives beside the title, so
          the bar only carries "See details" for non-viewer runs and is
          hidden otherwise. */}
      {!viewer && (
        <div style={{padding:'10px 16px',display:'flex',alignItems:'center',gap:10,flexWrap:'wrap',borderTop:'1px solid var(--border)'}}>
          <button type="button" onClick={openDetail} className="btn-ghost" style={{padding:'7px 14px',fontSize:12.5,fontWeight:600,borderRadius:999}}>
            ≣ See details
          </button>
        </div>
      )}
    </div>
  );
};

/**
 * Routed run-detail view (replaces the old fullscreen popup). Rendered
 * inline in the agents-detail pane at #agents/<agentId>/<runId>; the
 * breadcrumb carries the trail back, so there's no Close button. Left
 * pane = the live line + every process step; right pane = the result
 * preview shown directly (no nested container) with the deck/memo CTA.
 */
function RunDetailContent({ agentId, run, steps, reminders, viewer, previewUrl, inputsRows, liveMain, liveSub, liveColor, pct, isTerminal, currentStep, totalSteps, title, onChanged }) {
  const isDeck = !!viewer && viewer.kind === 'deck';
  // Converted-Markdown deliverables (Markdown Converter runs). Empty for
  // every other run — used to fill the result pane with download buttons
  // + the rendered Markdown when there's no deck/memo preview.
  const mdArtifacts = collectMarkdownArtifacts(steps);
  const blockCount = viewer?.blockCount || 0;
  const approvedCount = viewer?.approvedCount || 0;
  const generatedCount = viewer?.generatedCount || 0;
  const checkedCount = viewer?.checkedCount || 0;
  const regeneratedCount = viewer?.regeneratedCount || 0;

  // Keep the live/current step visible as the run advances from one step
  // to the next: scroll the active step back into view on each jump. The
  // step list lives in a scrollable pane that itself sits in the scrolling
  // page, so scrollIntoView (which walks every scroll ancestor) is used —
  // block:'nearest' brings it into view with minimal movement, no page jump.
  const activeStepRef = React.useRef(null);
  const activeStepIdx = steps.findIndex(s => s.status === 'running' || s.status === 'awaiting-human');
  React.useEffect(() => {
    const el = activeStepRef.current;
    if (el && typeof el.scrollIntoView === 'function') {
      el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [activeStepIdx, currentStep]);

  return (
    <div style={{background:'#fff',borderRadius:14,overflow:'hidden',border:'1px solid var(--border)',display:'flex',flexDirection:'column'}}>
      <div style={{display:'flex',alignItems:'center',gap:12,padding:'14px 18px',borderBottom:'1px solid var(--border)',flexWrap:'wrap'}}>
        <div style={{minWidth:0}}>
          <div style={{fontSize:11,color:'var(--fg-muted)'}}>{run.status} · started {relTime(run.startedAt)}{run.completedAt ? ` · finished ${relTime(run.completedAt)}` : ''}</div>
          <div style={{fontSize:18,fontWeight:600,letterSpacing:'-0.01em',wordBreak:'break-word'}}>{title}</div>
        </div>
        {viewer && (
          <DeckCtaButtons
            agentId={agentId}
            run={run}
            viewer={viewer}
            title={title}
            generationDone={run.status === 'done'}
          />
        )}
        <div style={{flex:1}}/>
        {viewer && (
          <span style={{fontSize:13,color:'var(--fg-muted)',whiteSpace:'nowrap'}}>
            {blockCount} {viewer.kind === 'deck' ? `slide${blockCount === 1 ? '' : 's'}` : `section${blockCount === 1 ? '' : 's'}`}
          </span>
        )}
        {isDeck && (
          <div style={{display:'flex',alignItems:'center',gap:6}} title="Slides by state — generated · checked (reviewer QA) · re-generated (manual) · approved">
            <span style={{fontSize:12,fontWeight:600,padding:'2px 9px',borderRadius:999,background:'#FFE7DC',color:'#B83C0F',whiteSpace:'nowrap'}}>◆ {generatedCount}</span>
            <span style={{fontSize:12,fontWeight:600,padding:'2px 9px',borderRadius:999,background:'#E6EEF8',color:'#1F5FA8',whiteSpace:'nowrap'}}>◉ {checkedCount}</span>
            <span style={{fontSize:12,fontWeight:600,padding:'2px 9px',borderRadius:999,background:'#FFE7DC',color:'#B83C0F',whiteSpace:'nowrap'}}>↻ {regeneratedCount}</span>
            <span style={{fontSize:12,fontWeight:600,padding:'2px 9px',borderRadius:999,background:'#DCF1E2',color:'#00702A',whiteSpace:'nowrap'}}>✓ {approvedCount}</span>
          </div>
        )}
      </div>
      <div style={{display:'flex',flexWrap:'wrap',minHeight:0}}>
        {/* LEFT: process */}
        <div style={{flex:'1 1 320px',minWidth:0,borderRight:'1px solid var(--border)',padding:'14px 18px',overflowY:'auto',maxHeight:'78vh'}}>
          {!isTerminal && (
            <div style={{background:'#FFE7DC',borderRadius:10,padding:'11px 12px',marginBottom:14}}>
              <div style={{fontSize:13.5,fontWeight:600,color:liveColor}}>{liveMain}</div>
              {liveSub && <div style={{fontSize:11.5,color:'#B83C0F',opacity:0.85,marginTop:3}}>{liveSub}</div>}
              <div style={{height:5,borderRadius:3,background:'rgba(184,60,15,0.18)',overflow:'hidden',marginTop:9}}>
                <div style={{height:'100%',width:`${pct}%`,background:'#B83C0F'}}/>
              </div>
              <div style={{fontSize:11,color:'#B83C0F',marginTop:5}}>step {currentStep} of {totalSteps} · {pct}%</div>
            </div>
          )}
          <div style={{fontSize:10.5,letterSpacing:'0.06em',textTransform:'uppercase',color:'var(--fg-muted)',fontWeight:600,marginBottom:8}}>Process — all steps</div>
          <ul style={{margin:0,padding:0,listStyle:'none',display:'flex',flexDirection:'column',gap:10}}>
            <RunInputsPanel description={run.description}/>
            {steps.map((s, i) => (
              <StepBoardItem
                key={s.id}
                step={s}
                stepNumber={inputsRows + 1 + i}
                agentId={agentId}
                runId={run.id}
                onChanged={onChanged}
                innerRef={i === activeStepIdx ? activeStepRef : null}
              />
            ))}
          </ul>
          <RemindersPanel reminders={reminders}/>
        </div>
        {/* RIGHT: result — the preview renders directly in the pane (no
            "Result" label, no nested bordered container). */}
        <div style={{flex:'1 1 360px',minWidth:0,padding:'14px 18px',background:'var(--bg-alt)',display:'flex',flexDirection:'column',gap:12}}>
          {previewUrl ? (
            <iframe title="result" src={previewUrl} sandbox="allow-same-origin allow-scripts" style={{width:'100%',height:'72vh',minHeight:420,border:0,borderRadius:10,background:'#fff'}}/>
          ) : mdArtifacts.files.length ? (
            <>
              <div style={{fontSize:10.5,letterSpacing:'0.06em',textTransform:'uppercase',color:'var(--fg-muted)',fontWeight:600}}>
                Converted Markdown — {mdArtifacts.files.length} file{mdArtifacts.files.length === 1 ? '' : 's'}
              </div>
              <MarkdownResultPanel artifacts={mdArtifacts}/>
            </>
          ) : (
            <div style={{fontSize:13,color:'var(--fg-muted)',padding:'20px 0'}}>No previewable result yet — the steps on the left show progress; artefacts appear when ready.</div>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Self-fetching wrapper for RunDetailContent. Polls the run while it's in
 * flight, sets the breadcrumb trail (Agents › <agent> › <run title>), and
 * renders the inline detail. Mounted by AgentsView at the routed URL.
 */
export function RunDetailRoute({ agentId, runId, agentName, onChanged }) {
  const [data, setData] = React.useState(null);
  const [error, setError] = React.useState(null);
  const [reloadKey, setReloadKey] = React.useState(0);
  const sawRunningRef = React.useRef(false);

  // Opening the full run detail counts as "seen" — drops the run out of
  // the Agents view's attention queue until it next updates.
  React.useEffect(() => { markAgentRunSeen(agentId, runId); }, [agentId, runId]);

  React.useEffect(() => {
    let cancelled = false;
    let timer = null;
    const tick = async () => {
      try {
        const result = await getAgentRun(agentId, runId);
        if (cancelled) return;
        setData(result); setError(null);
        // Poll until the run is terminal (see AgentRunInline) so the CTA's
        // "Open viewer" button auto-enables the moment run.status === 'done'.
        const runTerminal = result.run?.status === 'done' || result.run?.status === 'failed';
        const stepsInFlight = (result.steps || []).some(s => s.status === 'pending' || s.status === 'running' || s.status === 'awaiting-human');
        const running = !runTerminal || stepsInFlight;
        if (running) { sawRunningRef.current = true; timer = setTimeout(tick, 6000); }
        else if (sawRunningRef.current) { sawRunningRef.current = false; onChanged?.(); }
      } catch (err) {
        if (!cancelled) setError(err.message || String(err));
      }
    };
    tick();
    return () => { cancelled = true; if (timer) clearTimeout(timer); };
  }, [agentId, runId, reloadKey, onChanged]);

  const run = data?.run || null;
  const title = run?.title || 'Run';
  // The nav already renders the "Agents" crumb, so the sub-trail starts at
  // the agent name: Agents › <agent> › <run title>.
  useSubCrumbs([
    { label: agentName || 'Agent', href: `agents/${encodeURIComponent(agentId)}` },
    { label: title, href: null },
  ]);

  if (error) return <div className="page"><p style={{color:'#A20235',fontSize:13}}>Run fetch failed: {error}</p></div>;
  if (!run) return <div className="page"><p style={{color:'var(--fg-muted)',fontSize:13}}>Loading run…</p></div>;

  const steps = data.steps || [];
  const viewer = data.viewer || null;
  const reminders = data.reminders || [];
  const d = computeRunDisplay(agentId, run, steps, viewer);
  const reload = () => { setReloadKey(k => k + 1); onChanged?.(); };
  return (
    <RunDetailContent
      agentId={agentId}
      run={run}
      steps={steps}
      reminders={reminders}
      viewer={viewer}
      onChanged={reload}
      {...d}
    />
  );
}

/**
 * Split a deck-drafter step's markdown output into its per-slide blocks.
 * Mirrors splitDeckMarkdown() in functions/_lib/skills.ts — the heading
 * shape is `## Slide N — title`, with each block running to the next
 * heading. Anything before the first heading (the `<!-- deck-name -->`
 * marker / preamble) is dropped. Returns [] when the text has no slide
 * headings (e.g. the per-slide drafter's status-line result), so the
 * caller can fall back to the plain output view.
 */
function splitDeckSlides(md) {
  if (!md) return [];
  const heading = /^##\s+Slide\s+(\d+)\s*[—–-]\s*(.+?)\s*$/gim;
  const matches = [];
  let m;
  while ((m = heading.exec(md)) !== null) {
    matches.push({ index: parseInt(m[1], 10), title: m[2].trim(), start: m.index });
  }
  if (!matches.length) return [];
  return matches.map((s, i) => {
    const end = i + 1 < matches.length ? matches[i + 1].start : md.length;
    const block = md.slice(s.start, end).trim();
    const layoutMatch = /\*\*Layout:\*\*\s*([a-z-]+)/i.exec(block);
    return { index: s.index, title: s.title, layout: layoutMatch ? layoutMatch[1].toLowerCase() : null, markdown: block };
  });
}

/**
 * Renders the drafted deck as one card per slide (title + archetype chip
 * + the slide's drafted inputs) instead of a single collapsed blob, so
 * every slide's content is visible at a glance. Each card stays
 * individually collapsible but defaults open.
 */
const DeckDraftBreakdown = ({ slides }) => (
  <div style={{marginTop:6}}>
    <div style={{fontSize:10.5,letterSpacing:'0.06em',textTransform:'uppercase',color:'var(--fg-muted)',fontWeight:600,marginBottom:6}}>
      {slides.length} slide{slides.length === 1 ? '' : 's'} drafted
    </div>
    <div style={{display:'flex',flexDirection:'column',gap:6}}>
      {slides.map(sl => {
        const body = sl.markdown.replace(/^##\s+Slide\s+\d+\s*[—–-]\s*.+?(\n|$)/i, '').trim();
        return (
          <details key={sl.index} open style={{padding:'8px 10px',background:'var(--bg)',border:'1px solid var(--border)',borderRadius:6}}>
            <summary style={{cursor:'pointer',fontSize:12.5,fontWeight:600,display:'flex',alignItems:'center',gap:8,flexWrap:'wrap'}}>
              <span style={{wordBreak:'break-word',minWidth:0}}>Slide {sl.index} — {sl.title}</span>
              {sl.layout && <span style={{fontSize:9.5,padding:'1px 6px',borderRadius:4,background:'var(--bg-alt)',color:'var(--fg-muted)',textTransform:'uppercase',letterSpacing:'0.04em',fontWeight:600,flexShrink:0}}>{sl.layout}</span>}
            </summary>
            <div style={{marginTop:6,fontSize:12.5,lineHeight:1.5}}>
              <MarkdownView source={body || sl.markdown}/>
            </div>
          </details>
        );
      })}
    </div>
  </div>
);

/**
 * Result view for a finished skill step. Deck steps get richer rendering:
 *   - deck-drafter(-slidewise): a per-slide breakdown (all inputs visible)
 *   - slide-designer: the per-slide design summary shown inline (not hidden
 *     behind a collapsed toggle, since it IS the thing to read)
 * Every other skill keeps the compact collapsed "View skill output".
 */
const SkillResultView = ({ step }) => {
  const isDrafter = step.skillId === 'deck-drafter' || step.skillId === 'deck-drafter-slidewise';
  const slides = isDrafter ? splitDeckSlides(step.resultText) : [];
  if (slides.length > 1) return <DeckDraftBreakdown slides={slides}/>;

  if (step.skillId === 'slide-designer') {
    return (
      <div style={{margin:'6px 0 0',padding:10,background:'var(--bg)',border:'1px solid var(--border)',borderRadius:6,fontSize:13,lineHeight:1.5}}>
        <MarkdownView source={step.resultText}/>
      </div>
    );
  }

  return (
    <details style={{marginTop:6}}>
      <summary style={{fontSize:11,color:'var(--orange)',cursor:'pointer'}}>View skill output ({step.resultText.length} chars)</summary>
      <div style={{margin:'6px 0 0',padding:10,background:'var(--bg)',border:'1px solid var(--border)',borderRadius:6,fontSize:13,lineHeight:1.5,maxHeight:480,overflow:'auto'}}>
        <MarkdownView source={step.resultText}/>
      </div>
    </details>
  );
};

const StepBoardItem = ({ step, stepNumber, agentId, runId, onChanged, innerRef = null }) => {
  const sc = STEP_STATUS_COLOR[step.status] || STEP_STATUS_COLOR.pending;
  const isGate = isApprovalGate(step);
  return (
    <li ref={innerRef}>
      {/* Title first — the step name on its own full-width line so it
          wraps as prose. Everything else (status, type, skill id,
          duration, skip) sits on the curation row BELOW it. */}
      <strong style={{display:'block',fontSize:13,wordBreak:'break-word',lineHeight:1.35,marginBottom:4}}>{step.name}</strong>
      <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:4,flexWrap:'wrap'}}>
        <span style={{fontSize:10,padding:'2px 7px',borderRadius:4,background:'var(--bg-alt)',color:'var(--fg-muted)',textTransform:'uppercase',letterSpacing:'0.04em',fontWeight:600,flexShrink:0}}>step {stepNumber}</span>
        <span style={{fontSize:10,padding:'2px 7px',borderRadius:4,background:sc.bg,color:sc.fg,textTransform:'uppercase',letterSpacing:'0.04em',fontWeight:600,flexShrink:0}}>{step.status}</span>
        <span className="tag" style={{fontSize:10,flexShrink:0}}>{isGate ? 'gate' : step.type}</span>
        {step.skillId && <span style={{fontSize:10.5,fontFamily:'JetBrains Mono',color:'var(--fg-muted)',wordBreak:'break-all',minWidth:0}}>skill={step.skillId}</span>}
        {isGate && step.assigneeUserId && (
          <span style={{fontSize:10.5,color:'var(--fg-muted)'}}>assignee → {step.assigneeUserId.slice(0, 8)}</span>
        )}
        {/* Duration hint: actual elapsed time when the step has
            already run; otherwise the historical expected time
            (italic + orange per spec). Lets the user predict how
            long the run will take just from the trace. */}
        {(step.actualLabel || step.expectedLabel) && (
          <em style={{fontSize:10.5,color:'var(--orange)',fontStyle:'italic',marginLeft:'auto'}}>
            {step.actualLabel
              ? `took ${step.actualLabel}`
              : `≈ ${step.expectedLabel}`}
          </em>
        )}
        {(step.status === 'running' || step.status === 'pending') && (
          <button
            type="button"
            onClick={async () => {
              if (!window.confirm(`Skip this step? It will be marked failed; the runner continues with the next step.`)) return;
              try {
                await stopAgentRun(agentId, runId, { scope: 'step', stepIndex: step.index });
                if (onChanged) onChanged();
              } catch (err) {
                window.alert(`Skip failed: ${err.message || String(err)}`);
              }
            }}
            style={{
              all:'unset',cursor:'pointer',fontSize:10.5,fontWeight:600,
              color:'#A20235',padding:'1px 8px',borderRadius:4,
              border:'1px solid #FCE6E0',marginLeft:'auto',
            }}
            title="Mark this step failed and let the run advance to the next one."
          >Skip step</button>
        )}
      </div>
      {step.description && <div style={{fontSize:12.5,color:'var(--fg-muted)',lineHeight:1.5}}>{step.description}</div>}
      {step.type === 'human_task' && step.prompt && (
        <div style={{fontSize:12.5,color:'var(--fg-muted)',fontStyle:'italic',marginTop:3}}>"{step.prompt}"</div>
      )}
      {step.status === 'awaiting-human' && (
        <HumanAnswerForm agentId={agentId} runId={runId} step={step} onAnswered={onChanged}/>
      )}
      {step.status === 'done' && step.answer && (
        <div style={{marginTop:6,padding:8,background:'var(--bg)',border:'1px solid var(--border)',borderRadius:6,fontSize:12,whiteSpace:'pre-wrap'}}>
          <div style={{fontSize:10.5,letterSpacing:'0.06em',textTransform:'uppercase',color:'var(--fg-muted)',marginBottom:3,fontWeight:600}}>Answer</div>
          {step.answer}
        </div>
      )}
      {step.status === 'done' && step.resultText && step.type === 'skill' && (
        <>
          {(() => {
            const pdfs = Array.from(step.resultText.matchAll(/\((\/api\/photos\/u\/[^)]+\.pdf)\)/g)).map(m => m[1]);
            if (!pdfs.length) return null;
            return (
              <div style={{marginTop:6,display:'flex',flexWrap:'wrap',gap:6}}>
                {pdfs.map(url => (
                  <a
                    key={url}
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      display:'inline-flex',alignItems:'center',gap:6,
                      padding:'5px 10px',borderRadius:6,
                      background:'#A20235',color:'#fff',
                      textDecoration:'none',fontSize:11.5,fontWeight:600,
                    }}
                  >
                    <Icon name="file" size={11}/> Open / Download PDF
                  </a>
                ))}
              </div>
            );
          })()}
          <SkillResultView step={step}/>
        </>
      )}
      {step.errorMessage && (
        <div style={{marginTop:6,padding:8,background:'#FCE6E0',color:'#A20235',borderRadius:6,fontSize:12}}>{step.errorMessage}</div>
      )}
      {step.status === 'failed' && agentId && runId && (
        <RetryButton agentId={agentId} runId={runId} stepRunId={step.id} onRetried={onChanged}/>
      )}
    </li>
  );
};

const RetryButton = ({ agentId, runId, stepRunId, onRetried, label = '↻ Retry this step' }) => {
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState(null);
  const click = async () => {
    setBusy(true); setError(null);
    try {
      await retryAgentRunStep(agentId, runId, stepRunId);
      onRetried?.();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div style={{marginTop:6,display:'flex',gap:6,alignItems:'center',flexWrap:'wrap'}}>
      <button
        type="button"
        onClick={click}
        disabled={busy}
        className="btn-orange"
        style={{padding:'4px 10px',fontSize:11.5,fontWeight:600}}
      >
        {busy ? 'Retrying…' : label}
      </button>
      {error && <span style={{fontSize:11,color:'#A20235'}}>{error}</span>}
    </div>
  );
};

/**
 * Detect whether a human_task step is shaped like an approval gate
 * (Pitch Brief sign-off, Team approval, Storyline review, etc.). The
 * Proposal Builder prompt template has the agent emit human_task
 * steps for these specifically, and they all read like "Approve the
 * Pitch Brief" rather than open-ended questions. When we recognise
 * one we render an Approve / Reject card instead of a free-text
 * answer box — same backend (still posts a string to the answer
 * endpoint), much nicer UX.
 */
const GATE_PATTERNS = /\b(approve|approval|sign[-\s]?off|review|gate|confirm.*(proceed|continue|ok))\b/i;
function isApprovalGate(step) {
  if (step?.type !== 'human_task') return false;
  const haystack = `${step.name || ''} ${step.prompt || ''} ${step.description || ''}`;
  return GATE_PATTERNS.test(haystack);
}

export const HumanAnswerForm = ({ agentId, runId, step, onAnswered, reviewContent = null }) => {
  const [answer, setAnswer] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState(null);
  // Reject panel toggled when user picks "Reject" on a gate — shows
  // a labeled textarea for the rework note.
  const [rejectMode, setRejectMode] = React.useState(false);
  // Free-text mode lets a user override a gate detection if they
  // want to leave a longer comment instead of plain Approve.
  const [freeTextMode, setFreeTextMode] = React.useState(false);
  // "Change" mode (storyline gate only — when reviewContent is passed):
  // opens the reviewed content (briefing / storyline) in an editable
  // textarea so the user can revise the prompt directly, then save and
  // build from the edited version instead of leaving a rework note.
  const [changeMode, setChangeMode] = React.useState(false);
  const [editText, setEditText] = React.useState('');

  const isGate = isApprovalGate(step);
  // Only the storyline-style gate (deck creator) hands us the content
  // under review — that's the signal to offer the inline-edit "Change"
  // button. Other gates (proposal sign-offs) keep the reject/comment UI.
  const canEdit = isGate && typeof reviewContent === 'string' && reviewContent.trim().length > 0;

  const openChange = () => {
    setEditText(String(reviewContent || ''));
    setChangeMode(true);
  };

  const submit = async (overrideAnswer) => {
    const text = (overrideAnswer ?? answer).trim();
    if (!text) return;
    setBusy(true); setError(null);
    try {
      await answerAgentRunStep(agentId, runId, step.id, text);
      setAnswer('');
      setRejectMode(false);
      setFreeTextMode(false);
      setChangeMode(false);
      onAnswered?.();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  if (isGate && !freeTextMode) {
    // "Change" — edit the reviewed storyline/briefing inline, then save
    // and build the deck from the revised version.
    if (changeMode) {
      // One field — the whole storyline, edited in place, one Save.
      return (
        <form
          onSubmit={e => { e.preventDefault(); submit(`Approved with revisions — build the deck from THIS storyline, which supersedes the version under review above. Use it as the approved briefing / storyline, verbatim:\n\n${editText.trim()}`); }}
          style={{marginTop:10,display:'flex',flexDirection:'column',gap:8}}
        >
          <div style={{fontSize:12,color:'var(--fg-muted)'}}>
            Edit the whole storyline here — the deck is built from exactly this.
          </div>
          <textarea
            rows={16}
            value={editText}
            onChange={e => setEditText(e.target.value)}
            disabled={busy}
            autoFocus
            style={{width:'100%',boxSizing:'border-box',padding:'14px 16px',border:'1px solid var(--border)',borderRadius:12,background:'var(--bg)',color:'var(--fg)',fontSize:13.5,lineHeight:1.6,fontFamily:'inherit',resize:'vertical',minHeight:260}}
          />
          <div style={{display:'flex',gap:8,alignItems:'center'}}>
            <button
              type="submit"
              className="btn-orange"
              disabled={busy || !editText.trim()}
              style={{padding:'8px 20px',fontSize:13,fontWeight:600,borderRadius:8}}
              title="Save your edits and build the deck from this storyline"
            >
              {busy ? '…' : 'Save & build'}
            </button>
            <button
              type="button"
              className="btn-ghost"
              onClick={() => { setChangeMode(false); setEditText(''); }}
              disabled={busy}
              style={{padding:'8px 14px',fontSize:13,borderRadius:8}}
            >
              Cancel
            </button>
            {error && <span style={{fontSize:11.5,color:'#A20235'}}>{error}</span>}
          </div>
        </form>
      );
    }
    if (rejectMode) {
      return (
        <form
          onSubmit={e => { e.preventDefault(); submit(`Rejected: ${answer.trim()}`); }}
          style={{marginTop:8,display:'flex',flexDirection:'column',gap:6}}
        >
          <textarea
            className="textarea"
            rows={3}
            placeholder="What needs to change? The agent will rework based on this."
            value={answer}
            onChange={e => setAnswer(e.target.value)}
            disabled={busy}
            style={{fontSize:12.5}}
            autoFocus
          />
          <div style={{display:'flex',gap:6,alignItems:'center'}}>
            <button
              type="submit"
              className="btn-orange"
              disabled={busy || !answer.trim()}
              style={{padding:'6px 14px',fontSize:12,background:'#A20235'}}
            >
              {busy ? '…' : 'Send rework note'}
            </button>
            <button
              type="button"
              className="btn-ghost"
              onClick={() => { setRejectMode(false); setAnswer(''); }}
              disabled={busy}
              style={{padding:'6px 12px',fontSize:12}}
            >
              Cancel
            </button>
            {error && <span style={{fontSize:11,color:'#A20235'}}>{error}</span>}
          </div>
        </form>
      );
    }
    return (
      <div style={{marginTop:8,display:'flex',gap:6,alignItems:'center',flexWrap:'wrap'}}>
        <button
          type="button"
          className="btn-orange"
          onClick={() => submit('Approved.')}
          disabled={busy}
          style={{padding:'6px 14px',fontSize:12,fontWeight:600}}
          title="Sign off and let the agent continue"
        >
          {busy ? '…' : '✓ Approve'}
        </button>
        {canEdit ? (
          /* Storyline gate (deck creator): a single "Change" button that
             opens the storyline for inline editing, then saves and builds
             from the edited version. */
          <button
            type="button"
            className="btn-ghost"
            onClick={openChange}
            disabled={busy}
            style={{padding:'6px 14px',fontSize:12,fontWeight:600,color:'#B83C0F',border:'1px solid #B83C0F',borderRadius:6}}
            title="Edit the storyline, then save and build from your version"
          >
            ✎ Edit
          </button>
        ) : (
          <>
            <button
              type="button"
              className="btn-ghost"
              onClick={() => setRejectMode(true)}
              disabled={busy}
              style={{padding:'6px 12px',fontSize:12,color:'#A20235',border:'1px solid #A20235',borderRadius:6}}
              title="Send back for rework with a note"
            >
              ✕ Reject + note
            </button>
            <button
              type="button"
              className="btn-ghost"
              onClick={() => setFreeTextMode(true)}
              disabled={busy}
              style={{padding:'6px 12px',fontSize:11.5,color:'var(--fg-muted)'}}
              title="Write a longer answer instead of a plain approval"
            >
              Comment…
            </button>
          </>
        )}
        {error && <span style={{fontSize:11,color:'#A20235'}}>{error}</span>}
      </div>
    );
  }

  return (
    <form onSubmit={e => { e.preventDefault(); submit(); }} style={{marginTop:8,display:'flex',gap:6,alignItems:'flex-end',flexWrap:'wrap'}}>
      <textarea
        className="textarea"
        rows={2}
        placeholder="Your answer…"
        value={answer}
        onChange={e => setAnswer(e.target.value)}
        disabled={busy}
        style={{flex:1,fontSize:12.5,minWidth:240}}
      />
      <button type="submit" className="btn-orange" disabled={busy || !answer.trim()} style={{padding:'6px 12px',fontSize:12}}>
        {busy ? '…' : 'Submit'}
      </button>
      <button
        type="button"
        className="btn-ghost"
        onClick={() => submit('Skipped — proceed.')}
        disabled={busy}
        style={{padding:'6px 12px',fontSize:12}}
        title="Skip this checkpoint with a canned answer"
      >
        Skip
      </button>
      {error && <div style={{fontSize:11,color:'#A20235',width:'100%'}}>{error}</div>}
    </form>
  );
};

/* ─── Deadline reminders panel ───────────────────────────────────
 * Surfaces the Phase 4 scheduled reminders for a Proposal Builder
 * run. Hidden completely when no reminders exist — keeps the run
 * card clean for non-proposal-builder runs.
 */
const RemindersPanel = ({ reminders }) => {
  if (!reminders || reminders.length === 0) return null;

  const fmtDue = (ms) => {
    const d = new Date(ms);
    const datePart = d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
    const timePart = d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
    return `${datePart} · ${timePart}`;
  };
  const fmtKind = (k) => {
    if (k === 'T-14') return '14 days out';
    if (k === 'T-7')  return '1 week out';
    if (k === 'T-3')  return '3 days out';
    if (k === 'T-1')  return 'Day before';
    return k;
  };

  return (
    <div style={{padding:'10px 14px'}}>
      <div style={{fontSize:10.5,fontWeight:600,letterSpacing:'0.04em',textTransform:'uppercase',color:'var(--fg-muted)',marginBottom:8}}>
        Scheduled deadline reminders
      </div>
      <div style={{display:'flex',flexDirection:'column',gap:4}}>
        {reminders.map(r => {
          const fired = !!r.firedAt;
          return (
            <div key={r.id} style={{display:'flex',alignItems:'center',gap:10,fontSize:12.5}}>
              <span style={{
                fontSize:10.5,padding:'2px 8px',borderRadius:4,fontWeight:600,letterSpacing:'0.02em',
                background: fired ? '#DCF1E2' : '#FFE7DC',
                color:      fired ? '#00702A' : '#B83C0F',
              }}>
                {fired ? 'fired' : 'scheduled'}
              </span>
              <span style={{minWidth:90}}>{fmtKind(r.kind)}</span>
              <span style={{color:'var(--fg-muted)'}}>{fmtDue(r.dueAt)}</span>
              {fired && <span style={{color:'var(--fg-muted)',fontSize:11}}>· delivered {relTime(r.firedAt)}</span>}
            </div>
          );
        })}
      </div>
    </div>
  );
};
